import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Fuel, RefreshCw, X, Loader2, Clock, User, CheckCircle, XCircle, History } from 'lucide-react';

const fmt = (d) => d ? new Date(d).toLocaleString('pt-AO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Nunca actualizado';

const FuelBadge = ({ label, on }) => (
  <Badge className={on ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}>
    {on ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}{label}
  </Badge>
);

const HistoryModal = ({ data, loading, onClose }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="fuel-history-modal">
    <div className="bg-zinc-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="p-6 border-b border-zinc-700 flex justify-between items-center">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-sonangol-orange" /> Histórico · {data?.station_name}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-fuel-history-btn"><X className="w-5 h-5" /></Button>
      </div>
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-zinc-400" /></div>
      ) : !data?.history?.length ? (
        <p className="p-8 text-center text-zinc-400" data-testid="fuel-history-empty">Sem alterações registadas</p>
      ) : (
        <div className="p-4 space-y-2">
          {data.history.map(h => (
            <div key={h.id} className="bg-zinc-800 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`fuel-history-row-${h.id}`}>
              <div>
                <p className="text-white text-sm flex items-center gap-1"><Clock className="w-3 h-3 text-zinc-400" /> {fmt(h.updated_at)}</p>
                <p className="text-zinc-400 text-xs flex items-center gap-1 mt-1">
                  <User className="w-3 h-3" /> {h.updated_by_name || 'Funcionário'} {h.updated_by && `(${h.updated_by})`}
                </p>
              </div>
              <div className="flex gap-2">
                <FuelBadge label="Gasóleo" on={h.has_gasoleo} />
                <FuelBadge label="Gasolina" on={h.has_gasolina} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export const FuelHistoryTab = ({ api }) => {
  const [stations, setStations] = useState([]);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api().get('/super-admin/fuel/stations');
      setStations(res.data);
    } catch (err) {
      toast.error('Erro ao carregar combustível');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openHistory = async (station) => {
    setHistory({ station_name: station.name });
    setLoadingHistory(true);
    try {
      const res = await api().get(`/super-admin/fuel/stations/${station.id}/history`);
      setHistory(res.data);
    } catch (err) {
      toast.error('Erro ao carregar histórico');
    }
    setLoadingHistory(false);
  };

  return (
    <div className="space-y-4" data-testid="fuel-history-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-barlow font-bold text-white">Combustível · Sonangol DC+</h2>
        <Button onClick={load} variant="outline" className="bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700" data-testid="refresh-fuel-stations-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>
      <div className="grid gap-3">
        {stations.map(s => (
          <button key={s.id} onClick={() => openHistory(s)} data-testid={`fuel-station-row-${s.id}`}
            className="text-left bg-zinc-900 border border-zinc-700 rounded-xl p-4 hover:border-sonangol-orange transition-colors">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sonangol-orange/20 rounded-lg flex items-center justify-center">
                  <Fuel className="w-5 h-5 text-sonangol-orange" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{s.name}</h3>
                  <p className="text-zinc-400 text-xs">Última actualização: {fmt(s.fuel_updated_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <FuelBadge label="Gasóleo" on={s.has_gasoleo} />
                <FuelBadge label="Gasolina" on={s.has_gasolina} />
                <Badge className="bg-zinc-700 text-zinc-300" data-testid={`fuel-history-count-${s.id}`}>
                  <History className="w-3 h-3 mr-1" />{s.history_count} alterações
                </Badge>
              </div>
            </div>
          </button>
        ))}
      </div>
      {history && <HistoryModal data={history} loading={loadingHistory} onClose={() => setHistory(null)} />}
    </div>
  );
};

export default FuelHistoryTab;
